import { Component, OnInit, Output, EventEmitter, Input } from '@angular/core';
import { ViewChild, ElementRef, HostListener } from '@angular/core';

import { RecordsEventsModel, RecordModel, EventModel } from 'src/app/models/record.model';

import Konva from 'konva';
import * as moment from 'moment';

export interface TimelineConfig {
  width:      number;
  height:     number;
}

export interface DownloadTimeInterval {
  startTime:  Date;
  stopTime:   Date;
}

export type TimelineModeType = 'online' | 'archive' | 'download';

@Component({
  selector: 'app-timeline',
  templateUrl: './timeline.component.html',
  styleUrls: ['./timeline.component.scss'],
})
export class TimelineComponent implements OnInit {

  config:             TimelineConfig;
  _data:              RecordsEventsModel;
  _mode:              TimelineModeType = 'online';
  currentTime:        Date;
  downloadTimes:      DownloadTimeInterval = {startTime: new Date(), stopTime: new Date()};
  stage:              Konva.Stage;
  shapesLayer:        Konva.Layer;
  dataGroup:          Konva.Group;
  archiveGroup:       Konva.Group;
  hourBarsGroup:      Konva.Group;
  hoursBackground:    Konva.Rect;
  backgroundRect:     Konva.Rect;
  currentTimeLabel:   Konva.Label;
  currentTimeText:    Konva.Text;
  markLeftLabel:      Konva.Label;
  markLeftText:       Konva.Text;
  markRightLabel:     Konva.Label;
  markRightText:      Konva.Text;
  markRect:           Konva.Rect;
  dragTimerObj:       NodeJS.Timeout = null;

  scaleBy  = 1.1;
  lastDist = 0;
  prevZoom = 0;

  hourIntervals = [
    {
      width:    400,
      interval: 6 * 60,
      amount:   4,
      format:   'HH:mm'
    },
    {
      width:    800,
      interval: 4 * 60,
      amount:   6,
      format:   'HH:mm'
    },
    {
      width:    6500,
      interval: 1 * 60,
      amount:   24,
      format:   'HH:mm'
    },
    {
      width:    15000,
      interval: 30,
      amount:   48,
      format:   'HH:mm:ss'
    },
    {
      width:    30000,
      interval: 15,
      amount:   96,
      format:   'HH:mm:ss'
    },
    {
      width:    60000,
      interval: 5,
      amount:   288,
      format:   'HH:mm:ss'
    },
  ];

  @Input()
  set data(data: RecordsEventsModel) {
    // console.log('data: ', data);
    if (data !== undefined) {
      this._data = data;
      this.setCurrentTime(moment(data.date).toDate());
    }
    this.initializeStage();
  }
  @Input()
  set zoom(zoomValue: number) {
    if (this.stage === undefined) {
      return;
    }
    if (zoomValue > this.prevZoom) {
      this.setStageScale(true);
    } else if (zoomValue < this.prevZoom) {
      this.setStageScale(false);
    }
    this.prevZoom = zoomValue;
  }
  @Input()
  set mode(newMode: TimelineModeType) {
    // console.log('mode: ', newMode);
    if (this.stage === undefined) {
      // console.error('mode: Stage is undefined');
      return;
    }
    if (this._mode !== newMode) {
      this.setTimelineMode(newMode);
    }
  }
  @Input()
  set initialize(init: boolean) {
    console.log('timeline::initialize():', init);
    this.initializeStage();
  }
  @Output()                       currentTimeChange   = new EventEmitter<string>();
  @Output()                       downloadTimesChange = new EventEmitter<DownloadTimeInterval>();
  @ViewChild('timeline', {static: true}) timelineElement: ElementRef;
  @HostListener('window:resize')  onResize() {
    setTimeout(() => {
      console.log('timeline::window::resize()');
      this.initializeStage();
    }, 1000);
  }

  constructor() {}

  ngOnInit() {
    this.currentTime = moment('12:00', 'HH:mm').toDate();
    // Component initialization crunch
    setTimeout(() => {
      this.initializeStage();
    }, 10);
  }

  initializeStage() {
    if (!(
      this.timelineElement.nativeElement.offsetWidth &&
      this.timelineElement.nativeElement.offsetHeight
    )) {
      // console.error('initializeStage(): Timeline component is not initialized yet');
      setTimeout(() => {
        this.initializeStage();
      }, 10);
      return;
    }

    // Delete previous stage and all the children
    if (this.stage !== undefined) {
      this.stage.destroy();
    }

    this.config = {
      width:  this.timelineElement.nativeElement.offsetWidth,
      height: this.timelineElement.nativeElement.offsetHeight,
    };

    this.stage = new Konva.Stage({
      container:  this.timelineElement.nativeElement,
      width:      this.config.width,
      height:     this.config.height,
    });
    this.shapesLayer = new Konva.Layer();

    const stageWidth     = this.config.width;
    const backgroundRect = new Konva.Rect({
        x:          0,
        y:          35,
        width:      stageWidth,
        height:     10,
        name:       'backgroundRect',
        draggable:  false,
        fill:       'black'
    });
    this.backgroundRect = backgroundRect;
    this.dataGroup      = new Konva.Group({});
    this.archiveGroup   = new Konva.Group({id: 'archiveGroup'});

    this.dataGroup.add(this.backgroundRect);
    this.dataGroup.add(this.archiveGroup);

    // this.currentTime = moment('12:00', 'HH:mm').toDate();
    this.setTimelineMode(this._mode);

    this.createHourBars();
    this.createArchiveData();
    this.subscribeEvents();

    this.shapesLayer.add(this.dataGroup);
    this.stage.add(this.shapesLayer);
  }

  setTimelineMode(mode: TimelineModeType) {
    // console.log('setTimelineMode: ', mode);
    this._mode = mode;
    switch (mode) {
      case 'online':
        this.destroyCurrentTime();
        this.destroyDownloadMarks();
        break;

      case 'archive':
        this.destroyDownloadMarks();
        this.setCurrentTime(this.currentTime);
        this.createCurrentTime();
        break;

      case 'download':
        this.destroyCurrentTime();
        this.createDownloadMarks();
        break;

      default:
        console.error('Invalid timeline mode');
    }
    this.shapesLayer.batchDraw();
  }

  createHourBars() {
    // console.log('createHourBars():');
    const scaledWidth = this.stage.width() * this.stage.scaleX();
    const interval = this.hourIntervals.find(function (el) {
      return el.width >= scaledWidth;
    });

    const topOffset =
        this.backgroundRect.position().y +
        this.backgroundRect.height() + 1;

    if (this.hourBarsGroup !== undefined) {
      this.hourBarsGroup.destroy();
    }
    if (this.hoursBackground !== undefined) {
      this.hoursBackground.destroy();
    }

    this.hourBarsGroup   = new Konva.Group();
    this.hoursBackground = new Konva.Rect({
        x:          0,
        y:          topOffset,
        width:      this.config.width,
        height:     20,
        fill:       '#66635b'
    });

    const deltaX  = this.stage.width() / interval.amount;
    for (let index = 0; index < interval.amount; ++index) {
      const bar = new Konva.Rect({
        name:   'non-scalable',
        x:      deltaX * index,
        y:      topOffset,
        width:  2,
        height: 6,
        fill:   '#dbd3ba'
      });
      this.hourBarsGroup.add(bar);

      const hourText = moment({hour: 0})
        .add(moment.duration(interval.interval * index, 'minute'))
        .format(interval.format);
      const hourLabel = new Konva.Label({
        name:   'non-scalable',
        x:      deltaX * index,
        y:      topOffset + 8,
      });
      hourLabel.add(
        new Konva.Text({
          text:     hourText,
          fontSize: 10,
          fill:     '#dbd3ba'
        })
      );
      this.hourBarsGroup.add(hourLabel);
    }

    this.hourBarsGroup.add(new Konva.Rect({
      name:   'non-scalable',
      x:      this.config.width - 2,
      y:      topOffset,
      width:  2,
      height: 6,
      fill:   '#dbd3ba'
    }));

    this.shapesLayer.add(this.hoursBackground);
    this.shapesLayer.add(this.hourBarsGroup);
  }

  createCurrentTime() {
    // console.log('createCurrentTime(): ', this.currentTime);

    const stageWidth = this.config.width;
    const seconds    = this.getSeconds(this.currentTime);

    this.currentTimeLabel = new Konva.Label({
      name:      'non-scalable',
      x:         seconds * stageWidth / 86400,
      y:         35,
      scaleX:    1 / this.stage.scaleX(),
      draggable: true,
      dragBoundFunc: function (pos: any) {
        let newX = pos.x;

        this.getStage().find('#archiveGroup')[0].getChildren().each(
          function(shape: any) {
            if (shape.name().includes('record')) {
              const shapeX = shape.getClientRect().x;
              if (Math.abs(shapeX - newX) < 5) {
                newX = shapeX;
              }
            }
          });

        if (newX < 0) {
            newX = 0;
        } else if (newX > stageWidth) {
            newX = stageWidth;
        }
        return {
            x: newX,
            y: this.absolutePosition().y
        };
      }
    });

    this.currentTimeLabel.add(new Konva.Tag({
      fill:               '#66635b',
      stroke:             '#dbd3ba',
      strokeWidth:        1,
      pointerDirection:   'down',
      pointerWidth:       10,
      pointerHeight:      10,
      lineJoin:           'round',
      shadowColor:        'black',
      shadowBlur:         10,
      shadowOffset:       { x: 10, y: 10 },
      shadowOpacity:      0.5
    }));

    this.currentTimeText = new Konva.Text({
      text:       moment(this.currentTime).format('HH:mm:ss'),
      fontSize:   14,
      padding:    5,
      fill:       '#dbd3ba'
    });
    this.currentTimeLabel.add(this.currentTimeText);

    this.dataGroup.add(this.currentTimeLabel);

    const currentTimeLabel = this.currentTimeLabel;
    currentTimeLabel.on('mouseover', function() {
        document.body.style.cursor = 'pointer';
    });
    currentTimeLabel.on('dragmove', () => {
        const currentTimeString = this.createTimeString(currentTimeLabel.position().x);
        this.currentTimeText.text(currentTimeString);
        let newTime = moment(this.getDate(this.currentTime) + ' ' + currentTimeString);
        if (newTime > moment(this.currentTime).endOf('day')) {
          newTime = moment(this.currentTime).endOf('day');
        }
        this.currentTime = newTime.toDate();
    });
    currentTimeLabel.on('dragend', () => {
      if (this.dragTimerObj !== null) {
        clearTimeout(this.dragTimerObj);
        this.dragTimerObj = null;
      }
      this.dragTimerObj = setTimeout(() => {
        if (this._mode === 'archive') {
          if (this.isCurrentTimeWithinRecord()) {
            this.currentTimeChange.emit(this.currentTime.toISOString());
          } else {
            this.currentTimeChange.emit('error');
          }
        }
      }, 1000);
    });
  }

  isCurrentTimeWithinRecord() {
    let result = false;
    this._data.records.forEach(record => {
      const startTime = new Date(record.startTime);
      const stopTime  = new Date(record.stopTime);
      if (this.currentTime >= startTime && this.currentTime <= stopTime) {
        result = true;
      }
    });
    return result;
  }

  getDate(date: Date): string {
    return date.getFullYear() + '-' +
      ('0' + (date.getMonth() + 1)).slice(-2) + '-' +
      ('0' + date.getDate()).slice(-2);
  }

  setCurrentTime(time: Date) {
    // console.log('setCurrentTime(): Enter');
    if (time !== undefined && this.stage !== undefined) {

      // Find beginning of archive record close to input time (12:00)
      let minDiff = Number.MAX_SAFE_INTEGER;
      let minTime = time;
      this._data.records.forEach(record => {
        const startTime = moment(record.startTime);
        const diff = Math.abs(startTime.diff(time, 'seconds'));
        if (diff < minDiff) {
          minDiff = diff;
          minTime = startTime.toDate();
        }
      });

      this.currentTime = moment(minTime).toDate();
      // console.log('setCurrentTime(): ', this.currentTime);
      const posX = this.getSeconds(minTime) * this.stage.width() / 86400;
      if (this.currentTimeLabel !== undefined) {
        this.currentTimeLabel.x(posX);
      }
      if (this.currentTimeText !== undefined) {
        this.currentTimeText.text(this.createTimeString(posX));
      }

      if (this._mode === 'archive') {
        if (this.isCurrentTimeWithinRecord()) {
          this.currentTimeChange.emit(this.currentTime.toISOString());
        } else {
          this.currentTimeChange.emit('error');
        }
      }
    }
  }

  getSeconds(time: Date) {
    return time.getSeconds() + (60 * (time.getMinutes() + (60 * time.getHours())));
  }

  calculatePositionFromSeconds(seconds: number) {
    return seconds * this.stage.width() / 86400;
  }

  createTimeString(x: number) {
    const secondsTotal = Math.round(x * 86400 / this.config.width);
    const minutesLeft = secondsTotal % 3600;
    const currentTimeString =
        String(Math.floor(secondsTotal / 3600)).padStart(2, '0') + ':' +
        String(Math.floor(minutesLeft / 60)).padStart(2, '0') + ':' +
        String(minutesLeft % 60).padStart(2, '0');
    return currentTimeString;
  }

  destroyCurrentTime() {
    if (this.currentTimeLabel !== undefined) {
      this.currentTimeLabel.destroy();
    }
  }

  createMoment(date: Date, offsetX: number) {
    return moment(this.getDate(date) + ' ' + this.createTimeString(offsetX), 'YYYY-MM-DD HH:mm:ss');
  }

  createDownloadMarks() {
    let leftTime = moment(this.currentTime).subtract(15, 'minute');
    if (leftTime < moment(this.currentTime).startOf('day')) {
      leftTime = moment(this.currentTime).startOf('day');
    }
    this.downloadTimes.startTime = leftTime.toDate();
    const leftSeconds = this.getSeconds(leftTime.toDate());
    let rightTime = moment(this.currentTime).add(15, 'minute');
    if (rightTime > moment(this.currentTime).endOf('day')) {
      rightTime = moment(this.currentTime).endOf('day');
    }
    this.downloadTimes.stopTime = rightTime.toDate();
    const rightSeconds = this.getSeconds(rightTime.toDate());

    this.markLeftLabel = new Konva.Label({
      name:      'non-scalable',
      x:         this.calculatePositionFromSeconds(leftSeconds),
      y:         25,
      scaleX:    1 / this.stage.scaleX(),
      opacity:   0.75,
      draggable: true,
      dragBoundFunc: (pos: any) => {
          let newX = pos.x;
          if (newX < 0) {
              newX = 0;
          } else if (newX > this.markRightLabel.x()) {
              newX = this.markRightLabel.x();
          }
          return {
              x: newX,
              y: this.markLeftLabel.position().y
          };
      }
    });

    this.markLeftLabel.add(new Konva.Tag({
      fill:               '#95b9a3',
      stroke:             '#ffffff',
      strokeWidth:        1,
      pointerDirection:   'right',
      pointerWidth:       5,
      pointerHeight:      5,
      lineJoin:           'round',
    }));

    this.markLeftText = new Konva.Text({
      text:       leftTime.format('HH:mm:ss'),
      fontSize:   12,
      padding:    3,
      fill:       '#ffffff'
    });
    this.markLeftLabel.add(this.markLeftText);

    this.markRightLabel = new Konva.Label({
      name:      'non-scalable',
      x:         this.calculatePositionFromSeconds(rightSeconds),
      y:         25,
      scaleX:    1 / this.stage.scaleX(),
      opacity:   0.75,
      draggable: true,
      dragBoundFunc: (pos: any) => {
          let   newX = pos.x;
          if (newX < this.markLeftLabel.x()) {
              newX = this.markLeftLabel.x();
          } else if (newX > this.stage.width()) {
              newX = this.stage.width();
          }
          return {
              x: newX,
              y: this.markRightLabel.position().y
          };
      }
    });

    this.markRightLabel.add(new Konva.Tag({
      fill:               '#95b9a3',
      stroke:             '#ffffff',
      strokeWidth:        1,
      pointerDirection:   'left',
      pointerWidth:       5,
      pointerHeight:      5,
      lineJoin:           'round',
    }));

    this.markRightText = new Konva.Text({
      text:       rightTime.format('HH:mm:ss'),
      fontSize:   12,
      padding:    3,
      fill:       '#ffffff'
    });
    this.markRightLabel.add(this.markRightText);

    this.markRect = new Konva.Rect({
      x:        this.markLeftLabel.position().x,
      y:        25,
      width:    this.markRightLabel.position().x - this.markLeftLabel.position().x,
      height:   20,
      fill:     '#95b9a3',
      opacity:  0.75,
    });

    this.dataGroup.add(this.markLeftLabel);
    this.dataGroup.add(this.markRightLabel);
    this.dataGroup.add(this.markRect);

    this.markRect.moveToTop();
    this.downloadTimesChange.emit(this.downloadTimes);

    this.markLeftLabel.on('mouseover', function() {
        document.body.style.cursor = 'pointer';
    });
    this.markLeftLabel.on('dragmove', (event: any) => {
      event.evt.preventDefault();
      const markLeftString = this.createTimeString(this.markLeftLabel.position().x);
      this.markLeftText.text(markLeftString);
      // Check download interval to be 1.5 hours
      const markLeftX     = this.markLeftLabel.x();
      const markRightX    = this.markRightLabel.x();
      const markLeftTime  = this.createMoment(this.currentTime, markLeftX);
      const markRightTime = this.createMoment(this.currentTime, markRightX);
      const secondsDiff   = markRightTime.diff(markLeftTime, 'seconds');
      this.downloadTimes  = {startTime: markLeftTime.toDate(), stopTime: markRightTime.toDate()};
      if (secondsDiff > 5400) {
        const newRightSecs = markLeftTime.add(5400, 'seconds');
        this.downloadTimes.stopTime = newRightSecs.toDate();
        const newRightX    = this.calculatePositionFromSeconds(this.getSeconds(newRightSecs.toDate()));
        this.markRightText.text(this.createTimeString(newRightX));
        this.markRightLabel.x(newRightX);
      }
      this.markRect.x(this.markLeftLabel.x());
      this.markRect.width(this.markRightLabel.x() - this.markLeftLabel.x());
    });
    this.markLeftLabel.on('dragend', () => {
      if (this.dragTimerObj !== null) {
        clearTimeout(this.dragTimerObj);
        this.dragTimerObj = null;
      }
      this.downloadTimesChange.emit(this.downloadTimes);
    });

    this.markRightLabel.on('mouseover', function() {
      document.body.style.cursor = 'pointer';
    });
    this.markRightLabel.on('dragmove', () => {
      const markRightString = this.createTimeString(this.markRightLabel.position().x);
      this.markRightText.text(markRightString);
      // Check download interval to be 1.5 hours
      const markLeftX     = this.markLeftLabel.x();
      const markRightX    = this.markRightLabel.x();
      const markLeftTime  = this.createMoment(this.currentTime, markLeftX);
      const markRightTime = this.createMoment(this.currentTime, markRightX);
      const secondsDiff   = markRightTime.diff(markLeftTime, 'seconds');
      this.downloadTimes  = {startTime: markLeftTime.toDate(), stopTime: markRightTime.toDate()};
      if (secondsDiff > 5400) {
        const newLeftSecs = markRightTime.subtract(5400, 'seconds');
        this.downloadTimes.startTime = newLeftSecs.toDate();
        const newLeftX    = this.calculatePositionFromSeconds(this.getSeconds(newLeftSecs.toDate()));
        this.markLeftText.text(this.createTimeString(newLeftX));
        this.markLeftLabel.x(newLeftX);
      }
      this.markRect.x(this.markLeftLabel.x());
      this.markRect.width(this.markRightLabel.x() - this.markLeftLabel.x());
    });
    this.markRightLabel.on('dragend', () => {
      if (this.dragTimerObj !== null) {
        clearTimeout(this.dragTimerObj);
        this.dragTimerObj = null;
      }
      this.downloadTimesChange.emit(this.downloadTimes);
    });
  }

  destroyDownloadMarks() {
    if (this.markLeftLabel !== undefined) {
      this.markLeftLabel.destroy();
    }
    if (this.markRightLabel !== undefined) {
      this.markRightLabel.destroy();
    }
    if (this.markRect !== undefined) {
      this.markRect.destroy();
    }
  }

  subscribeEvents() {
    this.dataGroup.on('mouseover', function() {
        document.body.style.cursor = 'pointer';
    });
    this.dataGroup.on('mouseout', function() {
        document.body.style.cursor = 'default';
    });
    this.dataGroup.on('dblclick', function() {
      console.log('Timeline DBLCLICK event fired');
    });

    this.stageClickSubscribe();

    this.stage.on('wheel', (event: any) => {
      if (this._mode === 'online') {
        return;
      }
      event.evt.preventDefault();
      // const delta = event.evt.deltaY || event.evt.wheelDelta;

      const mousePointTo = {
        x: (this.stage.getPointerPosition().x - this.stage.x()) / this.stage.scaleX(),
        y: (this.stage.getPointerPosition().y - this.stage.y()) / this.stage.scaleY()
      };
      this.setStageScale(event.evt.deltaY > 0 || event.evt.deltaX > 0, mousePointTo);
    });

    this.stage.on('touchmove', (event: any) => {
      event.evt.preventDefault();
      this.stage.off('click');
      // console.log('subscribeEvents::touchmove: ', event.evt);

      const touch1 = event.evt.touches[0];
      const touch2 = event.evt.touches[1];

      if (touch1 && touch2) {
        const dist = this.getDistance(
          {
            x: touch1.clientX,
            y: touch1.clientY
          },
          {
            x: touch2.clientX,
            y: touch2.clientY
          }
        );

        console.log('subscribeEvents::dist: ', dist);

        if (!this.lastDist) {
          this.lastDist = dist;
        }

        this.setStageScale(dist > this.lastDist);
        this.lastDist = dist;
      }
    });

    this.stage.on('touchend', (event: any) => {
      event.evt.preventDefault();
      this.stageClickSubscribe();
      this.lastDist = 0;
    });
  }

  stageClickSubscribe() {
    this.stage.on('click', (event: any) => {
      if (this._mode !== 'archive') {
        return;
      }
      event.evt.preventDefault();

      const offsetX = (Math.abs(this.stage.position().x) + event.evt.offsetX) / this.stage.scaleX();
      const currentTimeString = this.createTimeString(offsetX);
      this.currentTimeText.text(currentTimeString);
      this.currentTimeLabel.position({
        x: offsetX,
        y: this.currentTimeLabel.position().y
      });
      this.currentTime = this.createMoment(this.currentTime, offsetX).toDate();
      if (this.dragTimerObj !== null) {
        clearTimeout(this.dragTimerObj);
        this.dragTimerObj = null;
      }
      this.dragTimerObj = setTimeout(() => {
        if (this._mode === 'archive') {
          this.currentTimeChange.emit(this.currentTime.toISOString());
        }
      }, 1000);
      this.shapesLayer.batchDraw();
    });
  }

  setStageScale(zoomIn: boolean, mousePointTo: any = null) {
    if (this.stage === undefined || this.currentTimeLabel === undefined) {
      return;
    }

    let offsetX = 0;
    if (mousePointTo === null) {
      if (this._mode === 'archive') {
        mousePointTo = {
          x: (this.currentTimeLabel.position().x - this.stage.x()) / this.stage.scaleX(),
          y: (this.currentTimeLabel.position().y - this.stage.y()) / this.stage.scaleY()
        };
        offsetX = this.currentTimeLabel.position().x;
      } else if (this._mode === 'download') {
        mousePointTo = {
          x: (this.markLeftLabel.position().x - this.stage.x()) / this.stage.scaleX(),
          y: (this.markLeftLabel.position().y - this.stage.y()) / this.stage.scaleY()
        };
        offsetX = this.markLeftLabel.position().x;
      }
    } else {
      offsetX = this.stage.getPointerPosition().x;
    }

    const oldScale = this.stage.scaleX();
    let   newScale = zoomIn ? oldScale * this.scaleBy : oldScale / this.scaleBy;
    if (newScale < 1) {
      newScale = 1;
    } else if (newScale > 30) {
      newScale = 30;
    }
    if (newScale === oldScale) {
      return;
    }

    const filterPos = (pos: any, scale: any) => {
      const stageWidth  = (this.stage.width()  * scale) - this.stage.width();

      return {
          y: 0,
          x: pos.x > 0 ? 0 : Math.abs(pos.x) > stageWidth ? -1 * stageWidth : pos.x,
      };
    };

    this.stage.setDraggable((newScale > 1));
    this.stage.dragBoundFunc((pos) => {
        return filterPos(pos, this.stage.scaleX());
    });

    this.stage.scale({ x: newScale, y: 1 });
    // console.log('newScale: ', newScale);

    const newPos = {
      x: -(mousePointTo.x - offsetX / newScale) * newScale,
      y: 0
    };
    // console.log('newPos: ', newPos);

    this.stage.position(filterPos(newPos, newScale));

    // Make text labels the same size
    this.createHourBars();
    if (this.markRect !== undefined) {
      this.markRect.moveToTop();
    }
    this.stage.find('.non-scalable').each((shape) => {
      shape.scaleX(1 / this.stage.scaleX());
    });

    // Adjust width for small records and events
    this.stage.find(node => {
      return node.name().search('record') !== -1;
    }).each((shape) => {
      const recordId = Number(shape.name().split(':', 2)[1]);
      const recordData = this._data.records.find(item => item.id === recordId);
      shape.width(this.getWidthByTimeInterval(recordData.stopTime, recordData.startTime));
    });
    this.stage.find(node => {
      return node.name().search('event') !== -1;
    }).each((shape) => {
      const eventId = Number(shape.name().split(':', 2)[1]);
      const eventData = this._data.events.find(item => item.id === eventId);
      shape.width(this.getWidthByTimeInterval(eventData.stopTime, eventData.startTime));
    });

    this.stage.batchDraw();
  }

  getDistance(p1: any, p2: any) {
    return Math.sqrt(Math.pow(p2.x - p1.x, 2) + Math.pow(p2.y - p1.y, 2));
  }

  getPositionByTime(time: string): number {
    // console.log('getPositionByTime(): ', this.currentTime);
    const dayStart = moment(new Date(this.currentTime).setHours(0, 0, 0, 0));
    const dayEnd   = moment(new Date(this.currentTime).setHours(23, 59, 59, 999));
    let inputTime  = moment(time);
    if (inputTime < dayStart) {
      inputTime = dayStart;
    } else if (inputTime > dayEnd) {
      inputTime = dayEnd;
    }

    const seconds = inputTime.diff(dayStart, 'seconds');

    const x = seconds * this.config.width / 86400;
    return x;
  }

  getWidthByTimeInterval(timeStop: string, timeStart: string): number {
    const width = this.getPositionByTime(timeStop) - this.getPositionByTime(timeStart);
    return width + 2 / this.stage.scaleX();
  }

  createArchiveData() {
    // console.log('createArchiveData(): ', this.currentTime);
    if (this.archiveGroup !== undefined) {
      this.archiveGroup.destroyChildren();
    }
    if (this._data !== undefined) {
      if (this._data.records.length) {
        this._data.records.forEach((record: RecordModel) => {
          const recordRect = new Konva.Rect({
            name:       'record:' + String(record.id),
            x:          this.getPositionByTime(record.startTime),
            y:          this.backgroundRect.y(),
            width:      this.getWidthByTimeInterval(record.stopTime, record.startTime),
            height:     this.backgroundRect.height(),
            draggable:  false,
            fill:       '#598392'
          });
          this.archiveGroup.add(recordRect);
        });
      }
      if (this._data.events.length) {
        this._data.events.forEach((event: EventModel) => {
          if (event !== undefined && event !== null) {
            // console.log('createArchiveData(): ', event.startTime);
            const eventRect = new Konva.Rect({
              name:       'event:' + String(event.id),
              x:          this.getPositionByTime(event.startTime),
              y:          this.backgroundRect.y(),
              width:      this.getWidthByTimeInterval(event.stopTime, event.startTime),
              height:     this.backgroundRect.height(),
              draggable:  false,
              fill:       '#d3776d'
            });
            this.archiveGroup.add(eventRect);
          }
        });
      }
      if (this.stage !== undefined) {
        this.stage.scaleX(1);
        this.stage.batchDraw();
      }
    }
  }

}
